import React, { useState, useEffect, useCallback } from 'react';
import { Users, Bell, Package, FileText, Calendar, AlertCircle, CheckCircle, Clock, Database, Shield, Heart, Phone, Settings, MapPin, Pill, RefreshCw, MessageCircle, Search, Plus, Truck, Star, CreditCard, Activity, BarChart3, TrendingUp, UserCheck, Stethoscope, ClipboardList, AlertTriangle, Info, DollarSign, Receipt, Download, Eye, LineChart, TrendingDown, LogOut, Lock, Home, Network, Wifi, WifiOff, MessageSquare, UserPlus, Globe, Zap, CheckSquare, Brain, Sparkles, Award, Target, Cloud, Building2, X, Check, XCircle, ChevronDown, Edit3, Save, FileCheck, Clipboard, UserMinus, CalendarCheck } from 'lucide-react';

// Note: This is a demo version without Firebase integration
// For production use, you'll need to set up Firebase configuration

const MedFastAgedCare = () => {
  // --- Demo Mode State ---
  const [isAuthReady, setIsAuthReady] = useState(true); // Set to true for demo
  const [db, setDb] = useState({}); // Mock database

  // --- App State ---
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [loginCredentials, setLoginCredentials] = useState({ username: '', password: '' });
  const [loginError, setLoginError] = useState('');

  // --- Data State ---
  const [facilities, setFacilities] = useState([
    { id: 'sunshine', name: 'Sunshine Aged Care Facility', address: '123 Care Street, Melbourne VIC 3000', phone: '(03) 9123 4567', totalResidents: 89, totalWards: 4, type: 'Large Facility' },
    { id: 'bayview', name: 'Bayview Gardens Aged Care', address: '456 Ocean Road, St Kilda VIC 3182', phone: '(03) 9876 5432', totalResidents: 56, totalWards: 3, type: 'Medium Facility' },
    { id: 'greenhill', name: 'Green Hill Residence', address: '789 Park Avenue, Brighton VIC 3186', phone: '(03) 9555 1234', totalResidents: 42, totalWards: 2, type: 'Boutique Facility' },
    { id: 'rosewood', name: 'Rosewood Care Community', address: '321 Garden Lane, Hawthorn VIC 3122', phone: '(03) 9444 7890', totalResidents: 78, totalWards: 4, type: 'Large Facility' }
  ]);

  const [residents, setResidents] = useState([
    { id: 1, name: 'Mary Elizabeth Johnson', room: 'A101', facility: 'sunshine', ward: 'ward_a', age: 78, dateOfBirth: '1947-03-15', admissionDate: '2023-08-12', careLevel: 'High', mobility: 'Wheelchair', cognitiveStatus: 'Mild Dementia', primaryDiagnosis: ['Type 2 Diabetes', 'Hypertension', 'Early Dementia'], secondaryConditions: ['Arthritis', 'Osteoporosis', 'Sleep Apnea'], allergies: ['Penicillin', 'Shellfish'], dietaryRestrictions: ['Diabetic Diet', 'Low Sodium'], weight: 68, height: 162, bloodType: 'O+', emergencyContact: { name: 'Sarah Johnson', relationship: 'Daughter', phone: '0412 345 678', email: 'sarah.johnson@email.com', familyConnectEnabled: true, lastLogin: '2025-07-23 14:30', notificationPreferences: ['medication_changes', 'health_updates', 'activities'] }, primaryPhysician: 'Dr. Robert Smith', careTeam: ['Nurse Sarah Mitchell', 'Care Worker Tom Brown'], prescriptions: [ { id: 'RX001', medication: 'Metformin 500mg', dosage: '500mg twice daily', doctor: 'Dr. Robert Smith', issued: '2025-06-01', expires: '2025-07-28', refillsLeft: 2, totalRefills: 5, status: 'Active', instructions: 'Take with meals', sideEffects: ['Nausea', 'Diarrhea'], lastTaken: '2025-07-24 08:00', adherenceRate: 95, automatedDispense: true }, { id: 'RX002', medication: 'Lisinopril 10mg', dosage: '10mg once daily', doctor: 'Dr. Robert Smith', issued: '2025-05-15', expires: '2025-07-25', refillsLeft: 0, totalRefills: 3, status: 'Expiring Soon', instructions: 'Take in morning', sideEffects: ['Dry cough', 'Dizziness'], lastTaken: '2025-07-24 06:00', adherenceRate: 98, automatedDispense: true } ], medicalHistory: [ { date: '2025-07-24', event: 'Blood glucose check', result: '6.8 mmol/L', status: 'Normal', doctor: 'Dr. Smith', automated: true }, { date: '2025-07-20', event: 'Blood glucose check', result: '7.2 mmol/L', status: 'Normal', doctor: 'Dr. Smith', automated: true }, { date: '2025-07-18', event: 'Blood pressure check', result: '135/80 mmHg', status: 'Elevated', doctor: 'Nurse Sarah', automated: false }, { date: '2025-07-15', event: 'Weight check', result: '68kg', status: 'Stable', doctor: 'Care Worker Tom', automated: false } ], doctorNotes: [ { id: 'DN001', date: '2025-07-24', doctor: 'Dr. Robert Smith', type: 'Progress Note', noteFormat: 'SOAP', content: 'Patient showing good glucose control with current Metformin dosage. Blood pressure remains slightly elevated but stable. Continue current medication regime.', subjective: 'Patient reports feeling well, no complaints of dizziness or fatigue. Appetite good.', objective: 'BP: 135/80, HR: 72 regular, Blood glucose: 6.8 mmol/L, Weight stable at 68kg', assessment: 'Type 2 DM well controlled. HTN stable but slightly elevated. Cognitive function stable.', plan: 'Continue current medications. Monitor BP daily. Review in 1 week.', priority: 'Normal', sharedWithFamily: true, collaboratingStaff: ['Nurse Sarah Mitchell'], followUpRequired: true, followUpDate: '2025-07-31' }, { id: 'DN002', date: '2025-07-10', doctor: 'Dr. Robert Smith', type: 'Alert', content: 'Elevated blood glucose reading (8.1 mmol/L). Discussed dietary compliance with care team. Monitor more frequently over next week.', priority: 'High', sharedWithFamily: true } ], lastAssessment: '2025-07-20', nextAssessment: '2025-07-27', fallRisk: 'High', cognitiveScore: 18, behavioralNotes: 'Agitated in evenings, responds well to music therapy', monthlyMedicationCost: 56.60, yearToDateCost: 385.40, familyUpdates: [ { date: '2025-07-23', type: 'medication', message: 'Metformin dose administered successfully', read: true }, { date: '2025-07-22', type: 'activity', message: 'Participated in group music therapy session', read: true }, { date: '2025-07-21', type: 'health', message: 'Blood sugar levels stable this week', read: false } ], carePlan: { goals: ['Maintain blood glucose within target range', 'Prevent falls', 'Maintain social engagement'], interventions: ['Daily glucose monitoring', 'Medication compliance monitoring', 'Music therapy 3x/week'], reviewDate: '2025-08-01' }, advanceCareDirective: true, resuscitationStatus: 'NFR' },
    { id: 2, name: 'Robert Chen', room: 'B102', facility: 'sunshine', ward: 'ward_b', age: 82, dateOfBirth: '1943-11-22', admissionDate: '2024-02-08', careLevel: 'Medium', mobility: 'Walking Frame', cognitiveStatus: 'Normal', primaryDiagnosis: ['Coronary Heart Disease', 'Osteoarthritis'], secondaryConditions: ['High Cholesterol', 'Chronic Back Pain'], allergies: ['Aspirin', 'Latex'], dietaryRestrictions: ['Heart Healthy Diet', 'Low Fat'], weight: 74, height: 175, bloodType: 'A+', emergencyContact: { name: 'Linda Chen', relationship: 'Wife', phone: '0423 456 789', email: 'linda.chen@email.com', familyConnectEnabled: true, lastLogin: '2025-07-24 09:15', notificationPreferences: ['medication_changes', 'health_updates'] }, primaryPhysician: 'Dr. Emily Wilson', careTeam: ['Nurse Jessica Lee', 'Physiotherapist Mark Davis'], prescriptions: [ { id: 'RX003', medication: 'Atorvastatin 20mg', dosage: '20mg once daily', doctor: 'Dr. Emily Wilson', issued: '2025-06-15', expires: '2025-08-15', refillsLeft: 3, totalRefills: 5, status: 'Active', instructions: 'Take at bedtime', sideEffects: ['Muscle pain', 'Liver issues'], lastTaken: '2025-07-23 22:00', adherenceRate: 99, automatedDispense: true } ], medicalHistory: [ { date: '2025-07-22', event: 'Physiotherapy session', result: 'Good progress', status: 'Improving', doctor: 'Mark Davis', automated: false }, { date: '2025-07-19', event: 'Cholesterol test', result: '4.2 mmol/L', status: 'Target achieved', doctor: 'Dr. Wilson', automated: true } ], doctorNotes: [ { id: 'DN003', date: '2025-07-22', doctor: 'Dr. Emily Wilson', type: 'Progress Note', noteFormat: 'SOAP', content: 'Patient continues to show improvement with physiotherapy. Cholesterol levels well controlled with current statin therapy. Mobility improving steadily.', subjective: 'Patient reports less pain when walking, better mobility with frame', objective: 'Cholesterol: 4.2 mmol/L, can walk 50m with frame, slight limp noted', assessment: 'CHD stable, cholesterol at target, mobility improving', plan: 'Continue statin, increase physio to 3x/week, review in 2 weeks', priority: 'Normal', sharedWithFamily: true } ], lastAssessment: '2025-07-15', nextAssessment: '2025-08-15', fallRisk: 'Medium', cognitiveScore: 28, behavioralNotes: 'Cooperative with care, enjoys social activities', monthlyMedicationCost: 42.30, yearToDateCost: 298.50, familyUpdates: [ { date: '2025-07-24', type: 'activity', message: 'Completed morning physiotherapy exercises', read: false }, { date: '2025-07-23', type: 'health', message: 'Mobility assessment shows improvement', read: true } ], carePlan: { goals: ['Improve mobility to 100m with frame', 'Maintain cholesterol <5.0', 'Prevent falls'], interventions: ['Physiotherapy 3x/week', 'Daily walking exercises', 'Cholesterol monitoring monthly'], reviewDate: '2025-08-15' }, advanceCareDirective: true, resuscitationStatus: 'Full' },
    { id: 3, name: 'Dorothy Margaret Williams', room: 'D205', facility: 'sunshine', ward: 'ward_d', age: 89, dateOfBirth: '1936-07-03', admissionDate: '2025-01-15', careLevel: 'Critical', mobility: 'Bed Bound', cognitiveStatus: 'Severe Dementia', primaryDiagnosis: ['Advanced Dementia', 'Palliative Care'], secondaryConditions: ['Dysphagia', 'Pressure Sores', 'Malnutrition'], allergies: ['None known'], dietaryRestrictions: ['Pureed Diet', 'Thickened Fluids'], weight: 52, height: 158, bloodType: 'B+', emergencyContact: { name: 'Michael Williams', relationship: 'Son', phone: '0434 567 890', email: 'michael.williams@email.com', familyConnectEnabled: true, lastLogin: '2025-07-24 11:00', notificationPreferences: ['all'] }, primaryPhysician: 'Dr. Patricia Taylor', careTeam: ['Palliative Nurse Emma White', 'Care Worker David Smith'], prescriptions: [ { id: 'RX004', medication: 'Morphine 5mg', dosage: '5mg every 4 hours PRN', doctor: 'Dr. Patricia Taylor', issued: '2025-07-01', expires: '2025-08-01', refillsLeft: 2, totalRefills: 2, status: 'Active', instructions: 'For pain management, monitor breathing', sideEffects: ['Drowsiness', 'Constipation'], lastTaken: '2025-07-24 10:00', adherenceRate: 100, automatedDispense: false, controlledDrug: true } ], medicalHistory: [ { date: '2025-07-23', event: 'Comfort assessment', result: 'Pain controlled', status: 'Stable', doctor: 'Dr. Taylor', automated: false }, { date: '2025-07-20', event: 'Family meeting', result: 'Care plan reviewed', status: 'Updated', doctor: 'Dr. Taylor', automated: false } ], doctorNotes: [ { id: 'DN004', date: '2025-07-23', doctor: 'Dr. Patricia Taylor', type: 'Palliative Review', noteFormat: 'Narrative', content: 'Family meeting held to discuss care goals. Patient comfortable, pain well controlled with current morphine regime. No signs of distress. Family supportive of comfort care approach.', priority: 'Normal', sharedWithFamily: true, collaboratingStaff: ['Emma White', 'Social Worker Jane'], followUpRequired: true, followUpDate: '2025-07-30' } ], lastAssessment: '2025-07-23', nextAssessment: '2025-07-25', fallRisk: 'N/A', cognitiveScore: 3, behavioralNotes: 'Responds to familiar voices, agitated when repositioned', monthlyMedicationCost: 125.80, yearToDateCost: 785.20, familyUpdates: [ { date: '2025-07-24', type: 'care', message: 'Comfort care provided, resident peaceful', read: false }, { date: '2025-07-23', type: 'visit', message: 'Family visit scheduled for tomorrow 2pm', read: true } ], carePlan: { goals: ['Maintain comfort', 'Support family', 'Dignity in end-of-life care'], interventions: ['Pain assessment 4-hourly', 'Repositioning 2-hourly', 'Family support meetings weekly'], reviewDate: '2025-07-30' }, advanceCareDirective: true, resuscitationStatus: 'NFR' }
  ]);

  const [medicationAlerts, setMedicationAlerts] = useState([
    { id: '1', type: 'critical', priority: 'High', facility: 'sunshine', resident: 'Mary Johnson', ward: 'Ward A', room: 'A101', medication: 'Lisinopril 10mg', issue: 'Prescription expires in 1 day', action: 'Contact Dr. Smith immediately', timestamp: '2025-07-24 09:30', status: 'Active', escalated: false, automatedWorkflow: true, workflowStatus: 'Doctor notified via ePrescribe system' },
    { id: '2', type: 'safety', priority: 'High', facility: 'sunshine', resident: 'Dorothy Williams', ward: 'Ward D', room: 'D205', medication: 'Morphine 5mg', issue: 'Missed dose - 6 hours overdue', action: 'Administer immediately, check pain levels', timestamp: '2025-07-24 10:15', status: 'Active', escalated: true, automatedWorkflow: false, workflowStatus: 'Manual intervention required' },
    { id: '3', type: 'interaction', priority: 'Medium', facility: 'sunshine', resident: 'Robert Chen', ward: 'Ward B', room: 'B102', medication: 'Atorvastatin + New prescription', issue: 'Potential drug interaction detected', action: 'Pharmacist review required', timestamp: '2025-07-24 08:45', status: 'Under Review', escalated: false, automatedWorkflow: true, workflowStatus: 'Pharmacist notified, AI analysis in progress' },
    { id: '4', type: 'supply', priority: 'Medium', facility: 'sunshine', resident: 'Multiple residents', ward: 'Ward A', room: 'Various', medication: 'Metformin 500mg', issue: 'Low stock - 3 days remaining', action: 'Process urgent bulk order', timestamp: '2025-07-24 07:00', status: 'Active', escalated: false, automatedWorkflow: true, workflowStatus: 'Auto-order initiated with connected pharmacy' }
  ]);

  const [wards, setWards] = useState([
    { id: 'ward_a', name: 'Ward A - Memory Care', capacity: 24, occupied: 22, careLevel: 'High', specialization: 'Dementia Care', activeAlerts: 2, pendingMeds: 5, connectedFamilies: 18, automatedWorkflows: 12, adherenceRate: 94.2 },
    { id: 'ward_b', name: 'Ward B - General Care', capacity: 28, occupied: 26, careLevel: 'Medium', specialization: 'General Aged Care', activeAlerts: 1, pendingMeds: 3, connectedFamilies: 21, automatedWorkflows: 10, adherenceRate: 96.8 },
    { id: 'ward_c', name: 'Ward C - Assisted Living', capacity: 20, occupied: 18, careLevel: 'Low', specialization: 'Independent Living', activeAlerts: 0, pendingMeds: 2, connectedFamilies: 15, automatedWorkflows: 8, adherenceRate: 98.1 },
    { id: 'ward_d', name: 'Ward D - Palliative Care', capacity: 16, occupied: 14, careLevel: 'Critical', specialization: 'End of Life Care', activeAlerts: 3, pendingMeds: 6, connectedFamilies: 14, automatedWorkflows: 15, adherenceRate: 91.5 }
  ]);

  const [bulkOrders, setBulkOrders] = useState([]);
  const [bulkOrderHistory, setBulkOrderHistory] = useState([]);
  const [pharmacistOrders, setPharmacistOrders] = useState([
    { id: 'PO001', facility: 'Sunshine Aged Care Facility', orderDate: '2025-07-24', residentCount: 5, totalValue: 487.50, priority: 'high', status: 'pending', medications: [ { medication: 'Metformin 500mg', quantity: 150, unitPrice: 0.45 }, { medication: 'Lisinopril 10mg', quantity: 100, unitPrice: 0.55 } ] },
    { id: 'PO002', facility: 'Bayview Gardens Aged Care', orderDate: '2025-07-23', residentCount: 3, totalValue: 325.00, priority: 'medium', status: 'accepted', acceptedBy: 'John Cooper', acceptedAt: '2025-07-23 14:30', medications: [ { medication: 'Atorvastatin 20mg', quantity: 90, unitPrice: 0.85 } ] }
  ]);
  const [facilityInvoices, setFacilityInvoices] = useState([]);
  const [automatedWorkflows, setAutomatedWorkflows] = useState([
    { id: 'WF001', name: 'Prescription Expiry Monitor', description: 'Automatically alerts 7 days before expiry', active: true, triggersToday: 12, errorReduction: '92%', timeSaved: '2 hours/day' },
    { id: 'WF002', name: 'Drug Interaction Checker', description: 'AI-powered interaction detection', active: true, triggersToday: 3, errorReduction: '99.2%', timeSaved: '45 min/day' }
  ]);
  const [controlledDrugRegister, setControlledDrugRegister] = useState([]);
  const [familyMessages, setFamilyMessages] = useState([]);

  // --- UI State ---
  const [activeTab, setActiveTab] = useState('dashboard');
  const [selectedFacility, setSelectedFacility] = useState('sunshine');
  const [selectedWard, setSelectedWard] = useState('all');
  const [selectedResident, setSelectedResident] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [alertFilter, setAlertFilter] = useState('all');
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showPatientHistory, setShowPatientHistory] = useState(null);
  const [networkStatus, setNetworkStatus] = useState('connected');
  const [showFamilyConnect, setShowFamilyConnect] = useState(false);
  const [showDrugRegister, setShowDrugRegister] = useState(false);
  const [showSupport, setShowSupport] = useState(false);
  const [showFacilitySelector, setShowFacilitySelector] = useState(false);
  const [orderStatusFilter, setOrderStatusFilter] = useState('all');
  const [showDoctorNote, setShowDoctorNote] = useState(false);
  const [editingNote, setEditingNote] = useState(null);
  const [noteType, setNoteType] = useState('progress');
  const [rejectionState, setRejectionState] = useState({ showModal: false, orderId: null, reason: '' });
  const [noteContent, setNoteContent] = useState({
    subjective: '',
    objective: '',
    assessment: '',
    plan: '',
  });
  
  // --- Hardcoded Data ---
  const users = [
    { id: 1, username: 'dr.smith', password: 'doctor123', role: 'doctor', name: 'Dr. Robert Smith', title: 'Senior Physician', specialization: 'Geriatric Medicine', facilities: ['sunshine', 'bayview'], permissions: ['view_all_patients', 'edit_prescriptions', 'view_medical_history', 'add_notes', 'eprescribe'] },
    { id: 2, username: 'dr.wilson', password: 'doctor123', role: 'doctor', name: 'Dr. Emily Wilson', title: 'General Practitioner', specialization: 'Aged Care', facilities: ['greenhill', 'rosewood'], permissions: ['view_all_patients', 'edit_prescriptions', 'view_medical_history', 'add_notes', 'eprescribe'] },
    { id: 3, username: 'nurse.sarah', password: 'nurse123', role: 'nurse', name: 'Sarah Mitchell', title: 'Registered Nurse', facility: 'sunshine', ward: 'ward_a', permissions: ['view_ward_patients', 'view_medications', 'report_alerts', 'view_schedules', 'drug_register'] },
    { id: 4, username: 'nurse.jessica', password: 'nurse123', role: 'nurse', name: 'Jessica Lee', title: 'Registered Nurse', facility: 'sunshine', ward: 'ward_b', permissions: ['view_ward_patients', 'view_medications', 'report_alerts', 'view_schedules', 'drug_register'] },
    { id: 5, username: 'admin', password: 'admin123', role: 'admin', name: 'Michael Davis', title: 'Network Administrator', facilities: ['all'], permissions: ['view_all', 'manage_users', 'view_analytics', 'manage_billing', 'approve_orders', 'system_settings'] },
    { id: 6, username: 'pharmacist.john', password: 'pharm123', role: 'pharmacist', name: 'John Cooper', title: 'Clinical Pharmacist', pharmacy: 'MedFast Connected Pharmacy', facilities: ['sunshine', 'bayview', 'greenhill', 'rosewood'], permissions: ['view_prescriptions', 'verify_orders', 'drug_interactions', 'dispense_medications', 'manage_orders'] },
    { id: 7, username: 'pharmacist.mary', password: 'pharm123', role: 'pharmacist', name: 'Mary Thompson', title: 'Senior Pharmacist', pharmacy: 'CarePlus Connected Pharmacy', facilities: ['sunshine', 'rosewood'], permissions: ['view_prescriptions', 'verify_orders', 'drug_interactions', 'dispense_medications', 'manage_orders'] }
  ];

  const wardStatistics = {
    ward_a: { totalMedications: 156, averagePerResident: 7.1, adherenceRate: 94.2, expiringThisWeek: 8, criticalAlerts: 2, monthlySpend: 3240.50, pbsSavings: 1120.25, connectedFamilies: 18, automatedWorkflows: 12, errorReduction: '89%', timesSaved: '45 min/day' },
    ward_b: { totalMedications: 142, averagePerResident: 5.5, adherenceRate: 96.8, expiringThisWeek: 4, criticalAlerts: 1, monthlySpend: 2890.75, pbsSavings: 980.50, connectedFamilies: 21, automatedWorkflows: 10, errorReduction: '92%', timesSaved: '38 min/day' },
    ward_c: { totalMedications: 98, averagePerResident: 5.4, adherenceRate: 98.1, expiringThisWeek: 2, criticalAlerts: 0, monthlySpend: 1650.25, pbsSavings: 540.75, connectedFamilies: 15, automatedWorkflows: 8, errorReduction: '95%', timesSaved: '30 min/day' },
    ward_d: { totalMedications: 87, averagePerResident: 6.2, adherenceRate: 91.5, expiringThisWeek: 5, criticalAlerts: 3, monthlySpend: 2100.00, pbsSavings: 650.00, connectedFamilies: 14, automatedWorkflows: 15, errorReduction: '85%', timesSaved: '52 min/day' }
  };

  const noteTemplates = {
    progress: { title: 'Progress Note', fields: ['subjective', 'objective', 'assessment', 'plan'] },
    consultation: { title: 'Consultation Note', fields: ['reason', 'history', 'examination', 'diagnosis', 'plan'] },
    medication: { title: 'Medication Review', fields: ['current_medications', 'effectiveness', 'side_effects', 'changes', 'monitoring'] },
    incident: { title: 'Incident Report', fields: ['incident_type', 'description', 'immediate_action', 'injuries', 'follow_up'] },
    discharge: { title: 'Discharge Summary', fields: ['admission_reason', 'hospital_course', 'discharge_diagnosis', 'discharge_medications', 'follow_up_instructions'] },
    palliative: { title: 'Palliative Care Review', fields: ['comfort_assessment', 'pain_management', 'family_communication', 'goals_of_care', 'plan'] }
  };

  // Simulated real-time updates
  useEffect(() => {
    const interval = setInterval(() => {
      setNetworkStatus(Math.random() > 0.95 ? 'syncing' : 'connected');
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  // --- Helper Functions ---
  const getCurrentFacility = () => {
    const facility = facilities.find(f => f.id === selectedFacility);
    if (!facility) return { name: 'Loading...', tagline: 'Connecting to MedFast Network...' };
    return {
      ...facility,
      tagline: 'Better medication outcomes for residents. Every day. Every time.',
      abn: '12 345 678 901',
      activeOrders: 23,
      expiringPrescriptions: 15,
      pendingRefills: 8,
      criticalAlerts: 3,
      monthlyMedicationBudget: 45000,
      currentMonthSpend: 32450,
      networkConnections: { pharmacies: 6, clinicians: 24, familyMembers: 142 },
      complianceScore: 98.5,
      incidentReduction: 76,
      timesSaved: '4.5 hours/day',
      errorReduction: '89%'
    };
  };

  const getWardsByFacility = (facilityId) => {
    // In demo mode, return wards for sunshine facility
    if (facilityId === 'sunshine') return wards;
    // Return empty array for other facilities in demo
    return [];
  };

  // --- Authentication Functions ---
  const handleLogin = () => {
    const user = users.find(u => u.username === loginCredentials.username && u.password === loginCredentials.password);
    if (user) {
      setCurrentUser(user);
      setIsAuthenticated(true);
      setLoginError('');
      setLoginCredentials({ username: '', password: '' });
      if (user.role === 'nurse') {
        setSelectedFacility(user.facility);
      } else if (user.facilities && user.facilities.length > 0 && user.facilities[0] !== 'all') {
        setSelectedFacility(user.facilities[0]);
      }
      if (user.role === 'pharmacist') setActiveTab('pharmacy');
      else if (user.role === 'doctor') setActiveTab('doctor');
    } else {
      setLoginError('Invalid username or password');
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setCurrentUser(null);
    setActiveTab('dashboard');
    setSelectedFacility('sunshine');
  };

  // --- Permission Checking ---
  const hasPermission = (permission) => {
    if (!currentUser) return false;
    return currentUser.permissions.includes(permission) || currentUser.permissions.includes('view_all');
  };

  const canAccessFacility = (facilityId) => {
    if (!currentUser) return false;
    if (currentUser.role === 'admin') return true;
    if (currentUser.role === 'nurse') return currentUser.facility === facilityId;
    if (currentUser.facilities) return currentUser.facilities.includes(facilityId) || currentUser.facilities.includes('all');
    return false;
  };

  const canViewResident = (resident) => {
    if (!currentUser) return false;
    if (currentUser.role === 'admin' || currentUser.role === 'doctor' || currentUser.role === 'pharmacist') return true;
    if (currentUser.role === 'nurse') {
      return resident.ward === currentUser.ward && resident.facility === currentUser.facility;
    }
    return false;
  };

  // --- Filter Functions ---
  const getFilteredResidents = () => {
    let filtered = residents.filter(r => r.facility === selectedFacility);
    if (currentUser?.role === 'nurse') {
      filtered = filtered.filter(resident => resident.ward === currentUser.ward);
    }
    if (selectedWard !== 'all') {
      filtered = filtered.filter(resident => resident.ward === selectedWard);
    }
    if (searchQuery) {
      filtered = filtered.filter(resident =>
        resident.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        resident.room.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (resident.primaryDiagnosis && resident.primaryDiagnosis.some(condition =>
          condition.toLowerCase().includes(searchQuery.toLowerCase())
        ))
      );
    }
    return filtered;
  };

  const getFilteredAlerts = () => {
    let filtered = medicationAlerts.filter(alert => alert.facility === selectedFacility);
    if (currentUser?.role === 'nurse') {
      const nurseWardName = wards.find(w => w.id === currentUser.ward)?.name;
      if (nurseWardName) {
        filtered = filtered.filter(alert => alert.ward === nurseWardName);
      }
    }
    if (alertFilter !== 'all') {
      filtered = filtered.filter(alert => alert.type === alertFilter);
    }
    return filtered.sort((a, b) => {
      const priorityOrder = { 'High': 3, 'Medium': 2, 'Low': 1 };
      return priorityOrder[b.priority] - priorityOrder[a.priority];
    });
  };

  const getFilteredPharmacistOrders = () => {
    let filtered = pharmacistOrders;
    if (orderStatusFilter !== 'all') {
      filtered = filtered.filter(order => order.status === orderStatusFilter);
    }
    return filtered.sort((a, b) => {
      const priorityOrder = { 'high': 3, 'medium': 2, 'low': 1 };
      if (a.status === 'pending' && b.status === 'pending') {
        return priorityOrder[b.priority] - priorityOrder[a.priority];
      }
      return new Date(b.orderDate) - new Date(a.orderDate);
    });
  };

  // --- Demo Mode Functions ---
  const addToBulkOrder = (resident, medication) => {
    const newOrder = {
      id: bulkOrders.length + 1,
      resident: resident.name,
      residentId: resident.id,
      ward: resident.ward,
      room: resident.room,
      medication: medication.medication,
      dosage: medication.dosage,
      quantity: 30,
      urgency: medication.refillsLeft === 0 ? 'High' : 'Medium',
      addedBy: currentUser?.name || 'Unknown',
      addedAt: new Date().toISOString(),
      prescriptionId: medication.id,
      doctor: medication.doctor,
      automatedOrder: false
    };
    setBulkOrders([...bulkOrders, newOrder]);
    alert(`${medication.medication} added to bulk order for ${resident.name}`);
  };

  const processBulkOrder = () => {
    const orderData = {
      id: `BO${Date.now()}`,
      orderDate: new Date().toISOString(),
      status: 'Pending Pharmacy',
      pharmacy: 'MedFast Connected Pharmacy',
      totalItems: bulkOrders.reduce((sum, item) => sum + item.quantity, 0),
      totalValue: Math.random() * 2000 + 500,
      processedBy: currentUser?.name,
      approvedBy: 'System',
      medications: bulkOrders,
      automatedOrder: true,
      pbsProcessed: false,
      facilityId: selectedFacility,
      createdAt: new Date().toISOString()
    };
    
    setBulkOrderHistory([...bulkOrderHistory, orderData]);
    alert('Bulk order processed and sent to network!');
    setBulkOrders([]);
  };

  const handleUpdatePharmacistOrder = (orderId, updateData) => {
    setPharmacistOrders(prevOrders => 
      prevOrders.map(order => 
        order.id === orderId ? { ...order, ...updateData } : order
      )
    );
    return true;
  };

  const handleAcceptOrder = async (orderId) => {
    const success = await handleUpdatePharmacistOrder(orderId, {
      status: 'accepted',
      acceptedBy: currentUser.name,
      acceptedAt: new Date().toISOString()
    });
    if (success) alert('Order accepted! Preparing medications for dispatch.');
  };

  const handleRejectOrder = async (orderId, reason) => {
    if (!reason) {
      alert("Rejection reason cannot be empty.");
      return;
    }
    const success = await handleUpdatePharmacistOrder(orderId, {
      status: 'rejected',
      rejectedBy: currentUser.name,
      rejectedAt: new Date().toISOString(),
      rejectionReason: reason
    });
    if (success) {
      alert('Order rejected. Notification sent to facility.');
      setRejectionState({ showModal: false, orderId: null, reason: '' });
    }
  };

  const handleCompleteOrder = async (orderId) => {
    const success = await handleUpdatePharmacistOrder(orderId, {
      status: 'completed',
      completedAt: new Date().toISOString(),
      deliveredBy: 'Express Courier'
    });
    if (success) alert('Order marked as completed! Delivery confirmation sent.');
  };

  const handleSaveNote = (patientId, noteData) => {
    const newNote = {
      id: `DN${Date.now()}`,
      date: new Date().toISOString().split('T')[0],
      doctor: currentUser.name,
      type: noteType,
      noteFormat: 'SOAP',
      ...noteData,
      priority: 'Normal',
      sharedWithFamily: false,
    };

    setResidents(prevResidents => 
      prevResidents.map(resident => 
        resident.id === patientId 
          ? { ...resident, doctorNotes: [...(resident.doctorNotes || []), newNote] }
          : resident
      )
    );

    alert('Clinical note saved successfully!');
    setShowDoctorNote(false);
    setEditingNote(null);
    setNoteContent({ subjective: '', objective: '', assessment: '', plan: '' });
  };

  const getDoctorDashboardData = () => {
    if (!currentUser || currentUser.role !== 'doctor') return { myPatients: [], recentNotes: [], pendingReviews: [], expiringPrescriptions: [], totalPatients: 0, notesToday: 0, upcomingReviews: 0, urgentPrescriptions: 0 };
    
    const myPatients = residents.filter(r => 
      r.primaryPhysician === currentUser?.name && 
      (currentUser?.facilities?.includes(r.facility) || currentUser?.facilities?.includes('all'))
    );
    
    const recentNotes = [];
    myPatients.forEach(patient => {
      patient.doctorNotes?.forEach(note => {
        if (note.doctor === currentUser?.name) {
          recentNotes.push({ ...note, patientName: patient.name, patientId: patient.id });
        }
      });
    });
    
    const pendingReviews = myPatients.filter(p => 
      p.nextAssessment && new Date(p.nextAssessment) <= new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)
    );
    
    const expiringPrescriptions = [];
    myPatients.forEach(patient => {
      patient.prescriptions?.forEach(rx => {
        if (rx.status === 'Expiring Soon' || (rx.expires && new Date(rx.expires) <= new Date(Date.now() + 3 * 24 * 60 * 60 * 1000))) {
          expiringPrescriptions.push({
            ...rx,
            patientName: patient.name,
            patientId: patient.id,
            room: patient.room
          });
        }
      });
    });
    
    return {
      myPatients,
      recentNotes: recentNotes.sort((a, b) => new Date(b.date) - new Date(a.date)).slice(0, 5),
      pendingReviews,
      expiringPrescriptions,
      totalPatients: myPatients.length,
      notesToday: recentNotes.filter(n => n.date === new Date().toISOString().split('T')[0]).length,
      upcomingReviews: pendingReviews.length,
      urgentPrescriptions: expiringPrescriptions.length
    };
  };

  const getAvailableTabs = () => {
    if (currentUser?.role === 'pharmacist') {
      return [
        { id: 'pharmacy', icon: Pill, label: 'Orders' },
        { id: 'dashboard', icon: Home, label: 'Overview' },
        { id: 'alerts', icon: AlertTriangle, label: 'Alerts' }
      ];
    }

    if (currentUser?.role === 'doctor') {
      return [
        { id: 'doctor', icon: Stethoscope, label: 'My Patients' },
        { id: 'residents', icon: Heart, label: 'All Residents' },
        { id: 'dashboard', icon: Home, label: 'Dashboard' },
        { id: 'alerts', icon: AlertTriangle, label: 'Alerts' }
      ];
    }
    
    const allTabs = [
      { id: 'dashboard', icon: Home, label: 'Dashboard' },
      { id: 'residents', icon: Heart, label: 'Residents' },
      { id: 'alerts', icon: AlertTriangle, label: 'Alerts' },
      { id: 'bulk_order', icon: Package, label: 'Orders' },
      { id: 'analytics', icon: BarChart3, label: 'Analytics' },
      { id: 'network', icon: Network, label: 'Network' }
    ];

    if (currentUser?.role === 'admin') {
      return [...allTabs, { id: 'payments', icon: Receipt, label: 'Payments' }];
    } else if (currentUser?.role === 'nurse') {
      return allTabs.filter(tab => ['dashboard', 'residents', 'alerts', 'bulk_order'].includes(tab.id));
    }
    return [];
  };

  // --- Render Functions ---
  const renderLogin = () => (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-50 via-blue-50 to-teal-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-8">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center space-x-2 mb-4">
            <h1 className="text-3xl font-bold bg-gradient-to-r from-green-600 to-teal-600 bg-clip-text text-transparent">MedFast</h1>
            <Heart className="h-8 w-8 text-green-500 animate-pulse" />
            <Network className="h-8 w-8 text-blue-500" />
          </div>
          <p className="text-gray-600 font-medium">Multi-Facility Medication Management Network</p>
          <p className="text-sm text-gray-500 mt-2">Better medication outcomes for residents.<br/>Every day. Every time.</p>
        </div>

        <div className="space-y-4">
          {loginError && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg animate-shake">
              {loginError}
            </div>
          )}
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Username
            </label>
            <input
              type="text"
              value={loginCredentials.username}
              onChange={(e) => setLoginCredentials({...loginCredentials, username: e.target.value})}
              onKeyPress={(e) => e.key === 'Enter' && handleLogin()}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all"
              placeholder="Enter username"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Password
            </label>
            <input
              type="password"
              value={loginCredentials.password}
              onChange={(e) => setLoginCredentials({...loginCredentials, password: e.target.value})}
              onKeyPress={(e) => e.key === 'Enter' && handleLogin()}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all"
              placeholder="Enter password"
            />
          </div>
          
          <button
            onClick={handleLogin}
            className="w-full bg-gradient-to-r from-green-600 to-teal-600 text-white py-3 rounded-lg hover:from-green-700 hover:to-teal-700 transition-all font-medium shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
          >
            <Lock className="h-4 w-4 inline mr-2" />
            Secure Login
          </button>
        </div>

        <div className="mt-6 space-y-4">
          <div className="flex items-center justify-center space-x-4 text-sm text-gray-600">
            <span className="flex items-center"><Shield className="h-4 w-4 mr-1 text-green-500" /> 256-bit Encryption</span>
            <span className="flex items-center"><Cloud className="h-4 w-4 mr-1 text-blue-500" /> Cloud Connected</span>
          </div>
          
          <div className="text-sm text-gray-600 bg-gray-50 rounded-lg p-3">
            <p className="font-semibold mb-2">Demo Accounts:</p>
            <div className="space-y-1 text-xs">
              <p><span className="font-medium">Doctor:</span> dr.smith / doctor123</p>
              <p><span className="font-medium">Nurse:</span> nurse.sarah / nurse123</p>
              <p><span className="font-medium">Admin:</span> admin / admin123</p>
              <p><span className="font-medium">Pharmacist:</span> pharmacist.john / pharm123</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderFacilitySelector = () => (
    <div className="bg-white border border-gray-200 rounded-lg p-2 mb-4">
      <button 
        onClick={() => setShowFacilitySelector(!showFacilitySelector)}
        className="w-full flex items-center justify-between p-2 hover:bg-gray-50 rounded"
      >
        <div className="flex items-center space-x-2">
          <Building2 className="h-5 w-5 text-gray-600" />
          <div className="text-left">
            <p className="font-medium text-sm">{getCurrentFacility().name}</p>
            <p className="text-xs text-gray-500">{getCurrentFacility().type}</p>
          </div>
        </div>
        <ChevronDown className={`h-4 w-4 text-gray-600 transition-transform ${showFacilitySelector ? 'rotate-180' : ''}`} />
      </button>
      
      {showFacilitySelector && (
        <div className="mt-2 space-y-1 border-t pt-2">
          {facilities.filter(f => canAccessFacility(f.id)).map(facility => (
            <button
              key={facility.id}
              onClick={() => {
                setSelectedFacility(facility.id);
                setShowFacilitySelector(false);
                setSelectedWard('all');
              }}
              className={`w-full p-2 text-left rounded hover:bg-gray-100 transition-colors ${
                selectedFacility === facility.id ? 'bg-green-50 border border-green-200' : ''
              }`}
            >
              <p className="font-medium text-sm">{facility.name}</p>
              <p className="text-xs text-gray-500">{facility.totalResidents} residents • {facility.totalWards} wards</p>
            </button>
          ))}
        </div>
      )}
    </div>
  );

  const renderDashboard = () => {
    const facilityInfo = getCurrentFacility();
    const currentWards = getWardsByFacility(selectedFacility);
    
    return (
      <div className="space-y-6">
        {(currentUser?.facilities?.length > 1 || currentUser?.role === 'admin') && renderFacilitySelector()}
        
        <div className="bg-gradient-to-r from-green-600 via-teal-600 to-blue-600 text-white p-6 rounded-xl shadow-lg">
          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-2xl font-bold mb-2">{facilityInfo.name}</h1>
              <p className="opacity-90 mb-1">{facilityInfo.tagline}</p>
              <div className="flex items-center space-x-4 text-sm opacity-90">
                <span className="flex items-center">
                  <Wifi className={`h-4 w-4 mr-1 ${networkStatus === 'connected' ? 'text-green-300' : 'text-yellow-300 animate-pulse'}`} />
                  {networkStatus === 'connected' ? 'Network Connected' : 'Syncing...'}
                </span>
                <span className="flex items-center">
                  <Shield className="h-4 w-4 mr-1" />
                  Secure & Compliant
                </span>
                <span className="flex items-center">
                  <Award className="h-4 w-4 mr-1" />
                  {facilityInfo.complianceScore}% Compliance
                </span>
              </div>
            </div>
            <div className="text-right">
              <p className="text-3xl font-bold">{facilityInfo.errorReduction}</p>
              <p className="text-sm opacity-90">Error Reduction</p>
            </div>
          </div>

          <div className="grid grid-cols-4 gap-4 mt-6">
            <div className="text-center">
              <p className="text-2xl font-bold">{facilityInfo.totalResidents}</p>
              <p className="text-sm opacity-90">Residents</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold">{facilityInfo.networkConnections.pharmacies}</p>
              <p className="text-sm opacity-90">Connected Pharmacies</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold">{facilityInfo.networkConnections.clinicians}</p>
              <p className="text-sm opacity-90">Active Clinicians</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold">{facilityInfo.networkConnections.familyMembers}</p>
              <p className="text-sm opacity-90">Family Connect Users</p>
            </div>
          </div>

          <div className="flex space-x-2 mt-4">
            {(currentUser?.role === 'admin' || currentUser?.role === 'nurse') && (
              <button 
                onClick={() => setActiveTab('bulk_order')}
                className="bg-white text-green-600 px-4 py-2 rounded-lg font-medium hover:bg-gray-100 transition-colors shadow-md"
              >
                <Zap className="h-4 w-4 inline mr-2" />
                Process Bulk Order
              </button>
            )}
            <button 
              onClick={() => setActiveTab('alerts')}
              className="border border-white text-white px-4 py-2 rounded-lg hover:bg-white hover:bg-opacity-20 transition-colors"
            >
              View All Alerts ({getFilteredAlerts().filter(a => a.status === 'Active').length})
            </button>
            <button 
              onClick={() => setShowSupport(true)}
              className="border border-white text-white px-4 py-2 rounded-lg hover:bg-white hover:bg-opacity-20 transition-colors"
            >
              <Phone className="h-4 w-4 inline mr-2" />
              24/7 Support
            </button>
          </div>
        </div>

        <div className="bg-gradient-to-r from-blue-50 to-green-50 border border-blue-200 p-4 rounded-lg">
          <h3 className="font-semibold text-blue-800 mb-3 flex items-center">
            <Sparkles className="h-5 w-5 mr-2" />
            Intelligent Automated Workflows Active
          </h3>
          <div className="grid grid-cols-2 gap-3">
            {automatedWorkflows.slice(0, 2).map(workflow => (
              <div key={workflow.id} className="bg-white rounded-lg p-3 border border-blue-100">
                <p className="font-medium text-sm">{workflow.name}</p>
                <div className="flex justify-between items-center mt-2">
                  <span className="text-xs text-green-600">Saves {workflow.timeSaved}</span>
                  <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">
                    {workflow.errorReduction} accurate
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-red-50 border border-red-200 p-4 rounded-lg">
          <h3 className="font-semibold text-red-800 mb-3 flex items-center">
            <AlertTriangle className="h-5 w-5 mr-2" />
            Critical Medication Alerts
          </h3>
          <div className="space-y-2">
            {getFilteredAlerts().filter(alert => alert.priority === 'High' && alert.status === 'Active').slice(0, 3).map(alert => (
              <div key={alert.id} className="flex justify-between items-center p-2 bg-white rounded border border-red-200">
                <div className="flex-1">
                  <p className="font-medium text-sm">{alert.resident} - {alert.room}</p>
                  <p className="text-xs text-red-600">{alert.issue}</p>
                  {alert.automatedWorkflow && (
                    <p className="text-xs text-blue-600 mt-1">
                      <Zap className="h-3 w-3 inline mr-1" />
                      {alert.workflowStatus}
                    </p>
                  )}
                </div>
                <button className="text-xs bg-red-600 text-white px-3 py-1 rounded hover:bg-red-700 transition-colors">
                  Action Required
                </button>
              </div>
            ))}
          </div>
        </div>

        <div>
          <h2 className="text-lg font-semibold mb-3">Connected Ward Network</h2>
          <div className="grid grid-cols-1 gap-3">
            {currentWards.filter(ward => 
              currentUser?.role === 'admin' || 
              currentUser?.role === 'doctor' || 
              currentUser?.role === 'pharmacist' ||
              (currentUser?.role === 'nurse' && ward.id === currentUser.ward)
            ).map(ward => (
              <div key={ward.id} className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow">
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <h3 className="font-semibold">{ward.name}</h3>
                    <p className="text-sm text-gray-600">{ward.specialization}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm">{ward.occupied}/{ward.capacity} occupied</p>
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      ward.careLevel === 'Critical' ? 'bg-red-100 text-red-800' :
                      ward.careLevel === 'High' ? 'bg-orange-100 text-orange-800' :
                      ward.careLevel === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-green-100 text-green-800'
                    }`}>
                      {ward.careLevel} Care
                    </span>
                  </div>
                </div>
                
                <div className="grid grid-cols-5 gap-2 mt-3 text-sm">
                  <div className="text-center">
                    <p className="font-semibold text-blue-600">{wardStatistics[ward.id]?.totalMedications || Math.round(100 + Math.random() * 50)}</p>
                    <p className="text-xs text-gray-600">Medications</p>
                  </div>
                  <div className="text-center">
                    <p className="font-semibold text-orange-600">{ward.activeAlerts}</p>
                    <p className="text-xs text-gray-600">Alerts</p>
                  </div>
                  <div className="text-center">
                    <p className="font-semibold text-green-600">{ward.adherenceRate}%</p>
                    <p className="text-xs text-gray-600">Adherence</p>
                  </div>
                  <div className="text-center">
                    <p className="font-semibold text-purple-600">{ward.connectedFamilies}</p>
                    <p className="text-xs text-gray-600">Families</p>
                  </div>
                  <div className="text-center">
                    <p className="font-semibold text-teal-600">{ward.automatedWorkflows}</p>
                    <p className="text-xs text-gray-600">Workflows</p>
                  </div>
                </div>
                
                <div className="flex space-x-2 mt-3">
                  <button 
                    onClick={() => {
                      setSelectedWard(ward.id);
                      setActiveTab('residents');
                    }}
                    className="flex-1 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                  >
                    Manage Ward
                  </button>
                  <button 
                    onClick={() => setShowDrugRegister(true)}
                    className="px-3 py-2 text-sm bg-blue-100 hover:bg-blue-200 text-blue-700 rounded-lg transition-colors"
                  >
                    <FileText className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {hasPermission('view_analytics') && (
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-200 p-4 rounded-lg">
              <Target className="h-8 w-8 text-blue-600 mb-2" />
              <h3 className="font-semibold text-blue-800">Incident Reduction</h3>
              <p className="text-2xl font-bold text-blue-900">{facilityInfo.incidentReduction}%</p>
              <p className="text-sm text-blue-600">Since implementation</p>
            </div>
            
            <div className="bg-gradient-to-br from-green-50 to-green-100 border border-green-200 p-4 rounded-lg">
              <Clock className="h-8 w-8 text-green-600 mb-2" />
              <h3 className="font-semibold text-green-800">Time Saved Daily</h3>
              <p className="text-2xl font-bold text-green-900">{facilityInfo.timesSaved}</p>
              <p className="text-sm text-green-600">Through automation</p>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderDoctorDashboard = () => {
    const dashboardData = getDoctorDashboardData();
    
    return (
      <div className="space-y-6">
        <div className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white p-6 rounded-xl shadow-lg">
          <h1 className="text-2xl font-bold mb-2">Doctor Dashboard</h1>
          <p className="opacity-90 mb-4">Clinical Management & Documentation</p>
          
          <div className="grid grid-cols-4 gap-4">
            <div className="text-center">
              <p className="text-3xl font-bold">{dashboardData.totalPatients}</p>
              <p className="text-sm opacity-90">My Patients</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold">{dashboardData.notesToday}</p>
              <p className="text-sm opacity-90">Notes Today</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold">{dashboardData.upcomingReviews}</p>
              <p className="text-sm opacity-90">Pending Reviews</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold">{dashboardData.urgentPrescriptions}</p>
              <p className="text-sm opacity-90">Expiring Scripts</p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <button 
            onClick={() => {
              setNoteType('progress');
              setShowDoctorNote(true);
            }}
            className="bg-blue-50 border border-blue-200 rounded-lg p-4 hover:bg-blue-100 transition-colors text-left"
          >
            <Edit3 className="h-6 w-6 text-blue-600 mb-2" />
            <h3 className="font-semibold text-blue-800">Write Progress Note</h3>
            <p className="text-sm text-blue-600">SOAP format documentation</p>
          </button>
          
          <button 
            onClick={() => {
              setNoteType('medication');
              setShowDoctorNote(true);
            }}
            className="bg-purple-50 border border-purple-200 rounded-lg p-4 hover:bg-purple-100 transition-colors text-left"
          >
            <Pill className="h-6 w-6 text-purple-600 mb-2" />
            <h3 className="font-semibold text-purple-800">Medication Review</h3>
            <p className="text-sm text-purple-600">Review & update prescriptions</p>
          </button>
        </div>

        {dashboardData.expiringPrescriptions.length > 0 && (
          <div className="bg-orange-50 border border-orange-200 p-4 rounded-lg">
            <h3 className="font-semibold text-orange-800 mb-3 flex items-center">
              <AlertCircle className="h-5 w-5 mr-2" />
              Prescriptions Requiring Renewal
            </h3>
            <div className="space-y-2">
              {dashboardData.expiringPrescriptions.map((rx, idx) => (
                <div key={idx} className="flex justify-between items-center p-2 bg-white rounded border border-orange-200">
                  <div>
                    <p className="font-medium text-sm">{rx.patientName} - {rx.room}</p>
                    <p className="text-xs text-orange-600">{rx.medication} expires {rx.expires}</p>
                  </div>
                  <button className="text-xs bg-orange-600 text-white px-3 py-1 rounded hover:bg-orange-700">
                    Renew
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        <div>
          <h2 className="text-lg font-semibold mb-3">My Patients</h2>
          <div className="space-y-3">
            {dashboardData.myPatients.map(patient => (
              <div key={patient.id} className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="font-semibold">{patient.name}</h3>
                    <p className="text-sm text-gray-600">
                      Room {patient.room} • Age {patient.age} • {patient.primaryDiagnosis.join(', ')}
                    </p>
                    <div className="flex items-center space-x-4 mt-2 text-xs">
                      <span className="flex items-center">
                        <CalendarCheck className="h-3 w-3 mr-1" />
                        Next Review: {patient.nextAssessment}
                      </span>
                      <span className={`px-2 py-1 rounded-full ${
                        patient.careLevel === 'Critical' ? 'bg-red-100 text-red-800' :
                        patient.careLevel === 'High' ? 'bg-orange-100 text-orange-800' :
                        patient.careLevel === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-green-100 text-green-800'
                      }`}>
                        {patient.careLevel} Care
                      </span>
                    </div>
                  </div>
                  <div className="flex space-x-2">
                    <button 
                      onClick={() => {
                        setSelectedResident(patient);
                        setNoteType('progress');
                        setShowDoctorNote(true);
                      }}
                      className="text-blue-600 hover:text-blue-700"
                    >
                      <Edit3 className="h-4 w-4" />
                    </button>
                    <button 
                      onClick={() => {
                        setShowPatientHistory(patient.id);
                        setActiveTab('residents');
                      }}
                      className="text-green-600 hover:text-green-700"
                    >
                      <Eye className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div>
          <h2 className="text-lg font-semibold mb-3">Recent Clinical Notes</h2>
          <div className="space-y-3">
            {dashboardData.recentNotes.map(note => (
              <div key={note.id} className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="font-medium text-sm">{note.patientName} - {note.type}</p>
                    <p className="text-xs text-gray-600">{note.date}</p>
                    <p className="text-sm mt-1">{note.content?.substring(0, 100)}...</p>
                  </div>
                  <span className={`text-xs px-2 py-1 rounded-full ${
                    note.priority === 'High' ? 'bg-red-100 text-red-800' :
                    'bg-blue-100 text-blue-800'
                  }`}>
                    {note.priority}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const renderDoctorNote = () => {
    const template = noteTemplates[noteType];
    const patient = selectedResident || (residents.length > 0 ? residents[0] : null);
    
    if(!patient) return null;
    
    const handleNoteInputChange = (e) => {
      const { name, value } = e.target;
      setNoteContent(prev => ({ ...prev, [name]: value }));
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg max-w-4xl w-full max-h-screen overflow-y-auto">
          <div className="p-6">
            <div className="flex justify-between items-start mb-6">
              <div>
                <h2 className="text-2xl font-bold flex items-center">
                  <ClipboardList className="h-6 w-6 mr-2 text-purple-600" />
                  {template.title}
                </h2>
                <p className="text-gray-600">Patient: {patient.name} - Room {patient.room}</p>
              </div>
              <button 
                onClick={() => {
                  setShowDoctorNote(false);
                  setEditingNote(null);
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">Note Type</label>
              <select 
                value={noteType}
                onChange={(e) => setNoteType(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
              >
                {Object.entries(noteTemplates).map(([key, value]) => (
                  <option key={key} value={key}>{value.title}</option>
                ))}
              </select>
            </div>

            {noteType === 'progress' && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Subjective</label>
                  <textarea 
                    name="subjective"
                    value={noteContent.subjective}
                    onChange={handleNoteInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    rows="3"
                    placeholder="What the patient reports..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Objective</label>
                  <textarea 
                    name="objective"
                    value={noteContent.objective}
                    onChange={handleNoteInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    rows="3"
                    placeholder="Observable clinical findings..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Assessment</label>
                  <textarea 
                    name="assessment"
                    value={noteContent.assessment}
                    onChange={handleNoteInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    rows="3"
                    placeholder="Clinical assessment and interpretation..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Plan</label>
                  <textarea 
                    name="plan"
                    value={noteContent.plan}
                    onChange={handleNoteInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    rows="3"
                    placeholder="Treatment plan and next steps..."
                  />
                </div>
              </div>
            )}

            <div className="flex justify-end space-x-3 mt-6">
              <button 
                onClick={() => setShowDoctorNote(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button 
                onClick={() => handleSaveNote(patient.id, noteContent)}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center"
              >
                <Save className="h-4 w-4 mr-2" />
                Save Note
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderResidents = () => {
    if (showPatientHistory) {
      const patient = residents.find(r => r.id === showPatientHistory);
      if (!patient || !canViewResident(patient)) return null;

      return (
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-xl font-bold">{patient.name}</h2>
              <p className="text-gray-600">Room {patient.room} • Comprehensive Medical Record</p>
            </div>
            <button 
              onClick={() => setShowPatientHistory(null)}
              className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors"
            >
              Back
            </button>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="flex justify-between items-start mb-4">
              <h3 className="font-semibold">Patient Information</h3>
              {patient.emergencyContact.familyConnectEnabled && (
                <span className="flex items-center text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">
                  <MessageSquare className="h-3 w-3 mr-1" />
                  Family Connected
                </span>
              )}
            </div>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p><span className="font-medium">Age:</span> {patient.age}</p>
                <p><span className="font-medium">DOB:</span> {patient.dateOfBirth}</p>
                <p><span className="font-medium">Blood Type:</span> {patient.bloodType}</p>
                <p><span className="font-medium">Care Level:</span> {patient.careLevel}</p>
                <p><span className="font-medium">Admission Date:</span> {patient.admissionDate}</p>
              </div>
              <div>
                <p><span className="font-medium">Mobility:</span> {patient.mobility}</p>
                <p><span className="font-medium">MMSE Score:</span> {patient.cognitiveScore}/30</p>
                <p><span className="font-medium">Fall Risk:</span> {patient.fallRisk}</p>
                <p><span className="font-medium">Weight:</span> {patient.weight}kg</p>
                <p><span className="font-medium">Resuscitation:</span> <span className={patient.resuscitationStatus === 'NFR' ? 'text-red-600' : 'text-green-600'}>{patient.resuscitationStatus}</span></p>
              </div>
            </div>
          </div>

          {patient.carePlan && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="font-semibold text-blue-800 mb-3 flex items-center">
                <Target className="h-5 w-5 mr-2" />
                Care Plan (Review: {patient.carePlan.reviewDate})
              </h3>
              <div className="space-y-2">
                <div>
                  <p className="text-sm font-medium text-blue-700">Goals:</p>
                  <ul className="text-sm text-gray-700 ml-4 list-disc">
                    {patient.carePlan.goals.map((goal, idx) => (
                      <li key={idx}>{goal}</li>
                    ))}
                  </ul>
                </div>
                <div>
                  <p className="text-sm font-medium text-blue-700">Interventions:</p>
                  <ul className="text-sm text-gray-700 ml-4 list-disc">
                    {patient.carePlan.interventions.map((intervention, idx) => (
                      <li key={idx}>{intervention}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}

          {patient.familyUpdates && patient.familyUpdates.length > 0 && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <h3 className="font-semibold text-green-800 mb-3 flex items-center">
                <MessageCircle className="h-5 w-5 mr-2" />
                Recent Family Updates
              </h3>
              <div className="space-y-2">
                {patient.familyUpdates.map((update, idx) => (
                  <div key={idx} className="bg-white rounded p-3 flex items-start justify-between">
                    <div>
                      <p className="text-sm">{update.message}</p>
                      <p className="text-xs text-gray-500 mt-1">{update.date}</p>
                    </div>
                    {!update.read && (
                      <span className="bg-blue-500 text-white text-xs px-2 py-1 rounded-full">New</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <h3 className="font-semibold mb-4 flex items-center">
              <Pill className="h-5 w-5 mr-2" />
              Current Medications
            </h3>
            <div className="space-y-3">
              {patient.prescriptions && patient.prescriptions.map((prescription) => (
                <div key={prescription.id} className="border border-gray-100 rounded-lg p-3">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <p className="font-medium">{prescription.medication}</p>
                      <p className="text-sm text-gray-600 mt-1">
                        {prescription.dosage} • {prescription.instructions}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        Prescribed by {prescription.doctor} • Expires {prescription.expires}
                      </p>
                      <div className="flex items-center space-x-2 mt-2">
                        {prescription.automatedDispense && (
                          <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full flex items-center">
                            <Zap className="h-3 w-3 mr-1" />
                            Auto-Dispensed
                          </span>
                        )}
                        {prescription.controlledDrug && (
                          <span className="text-xs bg-red-100 text-red-800 px-2 py-1 rounded-full flex items-center">
                            <Shield className="h-3 w-3 mr-1" />
                            Controlled
                          </span>
                        )}
                        <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">
                          {prescription.adherenceRate}% Adherence
                        </span>
                      </div>
                    </div>
                    <div className="text-right ml-4">
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        prescription.status === 'Active' ? 'bg-green-100 text-green-800' :
                        prescription.status === 'Expiring Soon' ? 'bg-orange-100 text-orange-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {prescription.status}
                      </span>
                      <p className="text-xs text-gray-500 mt-2">
                        {prescription.refillsLeft} refills left
                      </p>
                      {(currentUser?.role === 'admin' || currentUser?.role === 'nurse') && prescription.status === 'Expiring Soon' && (
                        <button 
                          onClick={() => addToBulkOrder(patient, prescription)}
                          className="mt-2 text-xs bg-blue-600 text-white px-2 py-1 rounded hover:bg-blue-700"
                        >
                          Add to Bulk
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <h3 className="font-semibold mb-4 flex items-center">
              <ClipboardList className="h-5 w-5 mr-2" />
              Recent Medical History
            </h3>
            <div className="space-y-2">
              {patient.medicalHistory && patient.medicalHistory.map((record, idx) => (
                <div key={idx} className="border border-gray-100 rounded p-3">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-medium text-sm flex items-center">
                        {record.event}
                        {record.automated && (
                          <span className="ml-2 text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                            <Brain className="h-3 w-3 inline mr-1" />
                            AI-Monitored
                          </span>
                        )}
                      </p>
                      <p className="text-sm text-gray-600">Result: {record.result}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-gray-500">{record.date}</p>
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        record.status === 'Normal' || record.status === 'Stable' ? 'bg-green-100 text-green-800' :
                        record.status === 'Elevated' ? 'bg-orange-100 text-orange-800' :
                        'bg-blue-100 text-blue-800'
                      }`}>
                        {record.status}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {(currentUser?.role === 'doctor' || currentUser?.role === 'admin') && (
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-semibold flex items-center">
                  <Stethoscope className="h-5 w-5 mr-2" />
                  Clinical Notes
                </h3>
                {currentUser?.role === 'doctor' && (
                  <button 
                    onClick={() => {
                      setSelectedResident(patient);
                      setNoteType('progress');
                      setShowDoctorNote(true);
                    }}
                    className="text-sm bg-purple-600 text-white px-3 py-1 rounded hover:bg-purple-700"
                  >
                    <Plus className="h-4 w-4 inline mr-1" />
                    Add Note
                  </button>
                )}
              </div>
              <div className="space-y-3">
                {patient.doctorNotes && patient.doctorNotes.map(note => (
                  <div key={note.id} className="border border-gray-100 rounded-lg p-3">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <h4 className="font-medium">{note.type}</h4>
                        <p className="text-sm text-gray-600">{note.doctor} • {note.date}</p>
                      </div>
                      <div className="flex items-center space-x-2">
                        {note.sharedWithFamily && (
                          <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                            <UserPlus className="h-3 w-3 inline mr-1" />
                            Family Shared
                          </span>
                        )}
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          note.priority === 'High' ? 'bg-red-100 text-red-800' :
                          note.priority === 'Urgent' ? 'bg-purple-100 text-purple-800' :
                          'bg-blue-100 text-blue-800'
                        }`}>
                          {note.priority}
                        </span>
                      </div>
                    </div>
                    {note.noteFormat === 'SOAP' && (
                      <div className="text-sm space-y-1 bg-gray-50 rounded p-2">
                        <p><span className="font-medium">S:</span> {note.subjective}</p>
                        <p><span className="font-medium">O:</span> {note.objective}</p>
                        <p><span className="font-medium">A:</span> {note.assessment}</p>
                        <p><span className="font-medium">P:</span> {note.plan}</p>
                      </div>
                    )}
                    {note.noteFormat === 'Narrative' && (
                      <p className="text-sm text-gray-700">{note.content}</p>
                    )}
                    {note.followUpRequired && (
                      <p className="text-xs text-orange-600 mt-2">
                        <CalendarCheck className="h-3 w-3 inline mr-1" />
                        Follow-up required by {note.followUpDate}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      );
    }

    return (
      <div className="space-y-4">
        {(currentUser?.facilities?.length > 1 || currentUser?.role === 'admin') && renderFacilitySelector()}
        
        <div className="flex justify-between items-center">
          <h2 className="text-xl font-bold">Connected Resident Network</h2>
          {currentUser?.role === 'admin' && (
            <button className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors">
              <Plus className="h-4 w-4 inline mr-2" />
              Add Resident
            </button>
          )}
        </div>

        <div className="space-y-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search residents by name, room, condition..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent"
            />
          </div>
          
          <div className="flex space-x-2 overflow-x-auto pb-2">
            <button
              onClick={() => setSelectedWard('all')}
              className={`px-4 py-2 rounded-full whitespace-nowrap text-sm transition-colors ${
                selectedWard === 'all' ? 'bg-green-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              All Wards ({getFilteredResidents().length})
            </button>
            {getWardsByFacility(selectedFacility).filter(ward => 
              currentUser?.role === 'admin' || 
              currentUser?.role === 'doctor' || 
              currentUser?.role === 'pharmacist' ||
              (currentUser?.role === 'nurse' && ward.id === currentUser.ward)
            ).map(ward => (
              <button
                key={ward.id}
                onClick={() => setSelectedWard(ward.id)}
                className={`px-4 py-2 rounded-full whitespace-nowrap text-sm transition-colors ${
                  selectedWard === ward.id ? 'bg-green-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {ward.name.split(' - ')[0]} ({residents.filter(r => r.ward === ward.id && canViewResident(r)).length})
              </button>
            ))}
          </div>
        </div>

        <div className="space-y-3">
          {getFilteredResidents().map(resident => (
            <div key={resident.id} className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow">
              <div className="flex justify-between items-start mb-3">
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-1">
                    <h3 className="font-semibold text-lg">{resident.name}</h3>
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      resident.careLevel === 'Critical' ? 'bg-red-100 text-red-800' :
                      resident.careLevel === 'High' ? 'bg-orange-100 text-orange-800' :
                      resident.careLevel === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-green-100 text-green-800'
                    }`}>
                      {resident.careLevel} Care
                    </span>
                    {resident.emergencyContact.familyConnectEnabled && (
                      <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                        <MessageSquare className="h-3 w-3 inline mr-1" />
                        Family Connected
                      </span>
                    )}
                    {resident.advanceCareDirective && (
                      <span className="text-xs bg-purple-100 text-purple-800 px-2 py-1 rounded-full">
                        <FileCheck className="h-3 w-3 inline mr-1" />
                        ACD
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-600">Room {resident.room} • Age {resident.age} • {resident.cognitiveStatus}</p>
                  <div className="flex flex-wrap gap-1 mt-2">
                    {resident.primaryDiagnosis && resident.primaryDiagnosis.slice(0, 3).map(condition => (
                      <span key={condition} className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                        {condition}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="flex space-x-2">
                  {currentUser?.role === 'doctor' && (
                    <button 
                      onClick={() => {
                        setSelectedResident(resident);
                        setNoteType('progress');
                        setShowDoctorNote(true);
                      }}
                      className="text-purple-600 hover:text-purple-700"
                      title="Add Note"
                    >
                      <Edit3 className="h-4 w-4" />
                    </button>
                  )}
                  <button 
                    onClick={() => setShowPatientHistory(resident.id)}
                    className="text-green-600 hover:text-green-700 font-medium text-sm"
                  >
                    View Details
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-4 gap-4 mb-3 text-sm">
                <div>
                  <p className="text-gray-600">Weight:</p>
                  <p className="font-medium">{resident.weight}kg</p>
                </div>
                <div>
                  <p className="text-gray-600">Fall Risk:</p>
                  <p className={`font-medium ${
                    resident.fallRisk === 'High' ? 'text-red-600' :
                    resident.fallRisk === 'Medium' ? 'text-orange-600' :
                    resident.fallRisk === 'Low' ? 'text-green-600' : 'text-gray-600'
                  }`}>
                    {resident.fallRisk}
                  </p>
                </div>
                <div>
                  <p className="text-gray-600">MMSE Score:</p>
                  <p className="font-medium">{resident.cognitiveScore}/30</p>
                </div>
                <div>
                  <p className="text-gray-600">Adherence:</p>
                  <p className="font-medium text-green-600">
                    {resident.prescriptions && resident.prescriptions.length > 0 ? Math.round(resident.prescriptions.reduce((acc, p) => acc + p.adherenceRate, 0) / resident.prescriptions.length) : 'N/A'}%
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderPharmacistDashboard = () => {
    const pendingOrders = getFilteredPharmacistOrders().filter(o => o.status === 'pending');
    const acceptedOrders = getFilteredPharmacistOrders().filter(o => o.status === 'accepted');
    const completedToday = getFilteredPharmacistOrders().filter(o => 
      o.status === 'completed' && o.completedAt?.startsWith(new Date().toISOString().split('T')[0])
    );

    return (
      <div className="space-y-6">
        <div className="bg-gradient-to-r from-purple-600 to-blue-600 text-white p-6 rounded-xl shadow-lg">
          <h1 className="text-2xl font-bold mb-2">Pharmacist Dashboard</h1>
          <p className="opacity-90 mb-4">Manage medication orders across facilities</p>
          
          <div className="grid grid-cols-4 gap-4">
            <div className="text-center">
              <p className="text-3xl font-bold">{pendingOrders.length}</p>
              <p className="text-sm opacity-90">Pending Orders</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold">{acceptedOrders.length}</p>
              <p className="text-sm opacity-90">In Progress</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold">{completedToday.length}</p>
              <p className="text-sm opacity-90">Completed Today</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold">{currentUser?.facilities?.length || 0}</p>
              <p className="text-sm opacity-90">Facilities Served</p>
            </div>
          </div>
        </div>

        <div className="flex space-x-2 overflow-x-auto pb-2">
          {['all', 'pending', 'accepted', 'completed', 'rejected'].map(status => (
            <button
              key={status}
              onClick={() => setOrderStatusFilter(status)}
              className={`px-4 py-2 rounded-full whitespace-nowrap text-sm transition-colors ${
                orderStatusFilter === status 
                  ? 'bg-purple-600 text-white' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {status.charAt(0).toUpperCase() + status.slice(1)} 
              ({getFilteredPharmacistOrders().filter(o => status === 'all' || o.status === status).length})
            </button>
          ))}
        </div>

        <div className="space-y-4">
          {getFilteredPharmacistOrders().map(order => (
            <div key={order.id} className={`bg-white border rounded-lg p-4 shadow-sm ${
              order.priority === 'high' ? 'border-red-200' : 'border-gray-200'
            }`}>
              <div className="flex justify-between items-start mb-3">
                <div>
                  <h3 className="font-semibold text-lg">{order.facility}</h3>
                  <p className="text-sm text-gray-600">
                    Order #{order.id.substring(0,8)} • {order.orderDate} • {order.residentCount} resident(s)
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-lg font-bold">${order.totalValue}</p>
                </div>
              </div>

              <div className="flex justify-end space-x-2">
                {order.status === 'pending' && (
                  <>
                    <button 
                      onClick={() => setRejectionState({ showModal: true, orderId: order.id, reason: '' })}
                      className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center"
                    >
                      <XCircle className="h-4 w-4 mr-2" />
                      Reject
                    </button>
                    <button 
                      onClick={() => handleAcceptOrder(order.id)}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center"
                    >
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Accept Order
                    </button>
                  </>
                )}
                {order.status === 'accepted' && (
                  <button 
                    onClick={() => handleCompleteOrder(order.id)}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center"
                  >
                    <CheckSquare className="h-4 w-4 mr-2" />
                    Mark as Completed
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>

        {getFilteredPharmacistOrders().length === 0 && (
          <div className="text-center py-8 bg-gray-50 rounded-lg">
            <Package className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-600">No orders found</p>
          </div>
        )}
      </div>
    );
  };

  const renderRejectionModal = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg p-6 w-full max-w-sm">
        <h3 className="text-lg font-bold mb-4">Rejection Reason</h3>
        <textarea
          value={rejectionState.reason}
          onChange={(e) => setRejectionState(prev => ({ ...prev, reason: e.target.value }))}
          className="w-full p-2 border border-gray-300 rounded-lg mb-4"
          rows="3"
          placeholder="Enter reason for rejection..."
        />
        <div className="flex justify-end space-x-2">
          <button
            onClick={() => setRejectionState({ showModal: false, orderId: null, reason: '' })}
            className="px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300"
          >
            Cancel
          </button>
          <button
            onClick={() => handleRejectOrder(rejectionState.orderId, rejectionState.reason)}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
          >
            Confirm Rejection
          </button>
        </div>
      </div>
    </div>
  );

  const renderAlerts = () => (
    <div className="space-y-4">
      <h2 className="text-xl font-bold">Medication Alerts</h2>
      
      <div className="flex space-x-2 overflow-x-auto pb-2">
        {['all', 'critical', 'safety', 'interaction', 'supply'].map(type => (
          <button
            key={type}
            onClick={() => setAlertFilter(type)}
            className={`px-4 py-2 rounded-full whitespace-nowrap text-sm transition-colors ${
              alertFilter === type ? 'bg-red-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {type.charAt(0).toUpperCase() + type.slice(1)} ({getFilteredAlerts().filter(a => type === 'all' || a.type === type).length})
          </button>
        ))}
      </div>

      <div className="space-y-3">
        {getFilteredAlerts().map(alert => (
          <div key={alert.id} className={`bg-white border rounded-lg p-4 shadow-sm ${
            alert.priority === 'High' ? 'border-red-300' : 'border-gray-200'
          }`}>
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <div className="flex items-center space-x-2 mb-2">
                  <span className={`text-xs px-2 py-1 rounded-full ${
                    alert.priority === 'High' ? 'bg-red-100 text-red-800' :
                    alert.priority === 'Medium' ? 'bg-orange-100 text-orange-800' :
                    'bg-yellow-100 text-yellow-800'
                  }`}>
                    {alert.priority}
                  </span>
                  <span className={`text-xs px-2 py-1 rounded-full ${
                    alert.type === 'critical' ? 'bg-purple-100 text-purple-800' :
                    alert.type === 'safety' ? 'bg-red-100 text-red-800' :
                    alert.type === 'interaction' ? 'bg-blue-100 text-blue-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {alert.type}
                  </span>
                  {alert.escalated && (
                    <span className="text-xs bg-red-500 text-white px-2 py-1 rounded-full">
                      Escalated
                    </span>
                  )}
                </div>
                <h3 className="font-semibold">{alert.resident} - {alert.room}</h3>
                <p className="text-sm text-gray-600 mt-1">{alert.ward} • {alert.medication}</p>
                <p className="text-sm mt-2">{alert.issue}</p>
                <p className="text-sm font-medium text-red-600 mt-1">{alert.action}</p>
                {alert.automatedWorkflow && (
                  <p className="text-xs text-blue-600 mt-2 flex items-center">
                    <Zap className="h-3 w-3 mr-1" />
                    {alert.workflowStatus}
                  </p>
                )}
                <p className="text-xs text-gray-500 mt-2">{alert.timestamp}</p>
              </div>
              <button className={`px-3 py-1 rounded-lg text-sm transition-colors ${
                alert.status === 'Active' 
                  ? 'bg-red-600 text-white hover:bg-red-700' 
                  : 'bg-gray-200 text-gray-600'
              }`}>
                {alert.status === 'Active' ? 'Resolve' : alert.status}
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderBulkOrder = () => (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-bold">Bulk Medication Order</h2>
        <span className="text-sm text-gray-600">{bulkOrders.length} items</span>
      </div>

      {bulkOrders.length === 0 ? (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center">
          <Package className="h-16 w-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-600 mb-4">No medications in bulk order</p>
          <button 
            onClick={() => setActiveTab('residents')}
            className="text-blue-600 hover:text-blue-700 font-medium"
          >
            Browse Residents to Add Medications
          </button>
        </div>
      ) : (
        <>
          <div className="space-y-3">
            {bulkOrders.map(order => (
              <div key={order.id} className="bg-white border border-gray-200 rounded-lg p-3">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <h4 className="font-medium">{order.medication}</h4>
                    <p className="text-sm text-gray-600">{order.resident} - {order.room}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      Qty: {order.quantity} • Added by {order.addedBy}
                    </p>
                  </div>
                  <div className="text-right">
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      order.urgency === 'High' ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {order.urgency}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h3 className="font-semibold text-blue-800 mb-2">Order Summary</h3>
            <div className="space-y-1 text-sm">
              <p>Total Items: {bulkOrders.reduce((sum, item) => sum + item.quantity, 0)}</p>
              <p>Unique Medications: {new Set(bulkOrders.map(o => o.medication)).size}</p>
              <p>High Priority Items: {bulkOrders.filter(o => o.urgency === 'High').length}</p>
            </div>
          </div>

          <button 
            onClick={processBulkOrder}
            className="w-full bg-green-600 text-white py-3 rounded-lg hover:bg-green-700 transition-colors font-medium shadow-lg"
          >
            <Truck className="h-4 w-4 inline mr-2" />
            Process Bulk Order
          </button>
        </>
      )}

      {bulkOrderHistory.length > 0 && (
        <div className="mt-8">
          <h3 className="font-semibold mb-3">Recent Order History</h3>
          <div className="space-y-2">
            {bulkOrderHistory.slice(-3).reverse().map(order => (
              <div key={order.id} className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="font-medium text-sm">{order.totalItems} items</p>
                    <p className="text-xs text-gray-600">{order.orderDate}</p>
                  </div>
                  <span className={`text-xs px-2 py-1 rounded-full ${
                    order.status === 'Delivered' ? 'bg-green-100 text-green-800' :
                    order.status === 'In Transit' ? 'bg-blue-100 text-blue-800' :
                    'bg-yellow-100 text-yellow-800'
                  }`}>
                    {order.status}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  const renderAnalytics = () => (
    <div className="space-y-6">
      <h2 className="text-xl font-bold">Network Analytics</h2>
      
      <div className="grid grid-cols-2 gap-4">
        <div className="bg-gradient-to-br from-green-50 to-green-100 border border-green-200 p-4 rounded-lg">
          <TrendingUp className="h-8 w-8 text-green-600 mb-2" />
          <h3 className="font-semibold text-green-800">Adherence Rate</h3>
          <p className="text-2xl font-bold text-green-900">96.4%</p>
          <p className="text-sm text-green-600">+2.3% this month</p>
        </div>
        
        <div className="bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-200 p-4 rounded-lg">
          <Activity className="h-8 w-8 text-blue-600 mb-2" />
          <h3 className="font-semibold text-blue-800">Error Prevention</h3>
          <p className="text-2xl font-bold text-blue-900">342</p>
          <p className="text-sm text-blue-600">Incidents prevented</p>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <h3 className="font-semibold mb-3">Monthly Trends</h3>
        <div className="space-y-3">
          <div className="flex justify-between items-center">
            <span className="text-sm">Medication Costs</span>
            <span className="text-sm font-medium">$45,230 (-12%)</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-sm">PBS Savings</span>
            <span className="text-sm font-medium text-green-600">$12,450</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-sm">Family Engagement</span>
            <span className="text-sm font-medium">87% (+5%)</span>
          </div>
        </div>
      </div>
    </div>
  );

  const renderNetwork = () => (
    <div className="space-y-6">
      <h2 className="text-xl font-bold">MedFast Network</h2>
      
      <div className="bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-lg p-4">
        <h3 className="font-semibold text-purple-800 mb-3">Connected Services</h3>
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-white rounded-lg p-3 border border-purple-100">
            <Globe className="h-6 w-6 text-purple-600 mb-2" />
            <p className="font-medium text-sm">6 Pharmacies</p>
            <p className="text-xs text-gray-600">Real-time inventory</p>
          </div>
          <div className="bg-white rounded-lg p-3 border border-purple-100">
            <Users className="h-6 w-6 text-blue-600 mb-2" />
            <p className="font-medium text-sm">24 Clinicians</p>
            <p className="text-xs text-gray-600">Secure messaging</p>
          </div>
        </div>
      </div>

      <div className="space-y-3">
        <h3 className="font-semibold">Network Status</h3>
        <div className="space-y-2">
          <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
            <span className="text-sm font-medium">ePrescribe Gateway</span>
            <span className="text-xs bg-green-600 text-white px-2 py-1 rounded-full">Online</span>
          </div>
          <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
            <span className="text-sm font-medium">PBS Integration</span>
            <span className="text-xs bg-green-600 text-white px-2 py-1 rounded-full">Online</span>
          </div>
          <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
            <span className="text-sm font-medium">Family Connect</span>
            <span className="text-xs bg-green-600 text-white px-2 py-1 rounded-full">Online</span>
          </div>
        </div>
      </div>
    </div>
  );

  const renderPayments = () => (
    <div className="space-y-6">
      <h2 className="text-xl font-bold">Billing & Payments</h2>
      
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <h3 className="font-semibold mb-3">Current Month Summary</h3>
        <div className="space-y-2">
          <div className="flex justify-between">
            <span className="text-sm">Total Medication Costs</span>
            <span className="font-medium">$32,450.25</span>
          </div>
          <div className="flex justify-between">
            <span className="text-sm">PBS Rebates</span>
            <span className="font-medium text-green-600">-$8,240.50</span>
          </div>
          <div className="border-t pt-2 flex justify-between">
            <span className="font-medium">Net Payable</span>
            <span className="font-bold">$24,209.75</span>
          </div>
        </div>
      </div>

      <div className="space-y-3">
        <h3 className="font-semibold">Recent Invoices</h3>
        {facilityInvoices.length === 0 ? (
          <p className="text-sm text-gray-600">No invoices available</p>
        ) : (
          facilityInvoices.map(invoice => (
            <div key={invoice.id} className="bg-gray-50 rounded-lg p-3">
              <div className="flex justify-between items-center">
                <div>
                  <p className="font-medium text-sm">Invoice #{invoice.id}</p>
                  <p className="text-xs text-gray-600">{invoice.date}</p>
                </div>
                <span className={`text-xs px-2 py-1 rounded-full ${
                  invoice.status === 'Paid' ? 'bg-green-100 text-green-800' :
                  'bg-yellow-100 text-yellow-800'
                }`}>
                  {invoice.status}
                </span>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );

  const renderDrugRegister = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-screen overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold">Controlled Drug Register</h2>
            <button onClick={() => setShowDrugRegister(false)} className="text-gray-500 hover:text-gray-700">
              <X className="h-5 w-5" />
            </button>
          </div>
          <p className="text-gray-600 mb-4">Digital S8 Drug Register - Real-time tracking and compliance</p>
          
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
            <p className="text-sm text-blue-800">
              <Shield className="h-4 w-4 inline mr-1" />
              All entries are digitally signed and blockchain-verified for regulatory compliance
            </p>
          </div>

          <div className="space-y-4">
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="font-semibold mb-2">Recent Entries</h3>
              <p className="text-sm text-gray-600">No controlled drug movements in the last 24 hours</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderFamilyConnect = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-screen overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold">Family Connect Portal</h2>
            <button onClick={() => setShowFamilyConnect(false)} className="text-gray-500 hover:text-gray-700">
              <X className="h-5 w-5" />
            </button>
          </div>
          <p className="text-gray-600">Secure communication with resident families</p>
        </div>
      </div>
    </div>
  );

  const renderSupport = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-md w-full">
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold">24/7 Support</h2>
            <button onClick={() => setShowSupport(false)} className="text-gray-500 hover:text-gray-700">
              <X className="h-5 w-5" />
            </button>
          </div>
          
          <div className="space-y-4">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <Phone className="h-6 w-6 text-green-600 mb-2" />
              <h3 className="font-semibold text-green-800">Emergency Support</h3>
              <p className="text-2xl font-bold text-green-900">1800 MEDFAST</p>
              <p className="text-sm text-green-600">Available 24/7</p>
            </div>
            
            <div className="space-y-2">
              <button className="w-full text-left p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                <p className="font-medium">Technical Support</p>
                <p className="text-sm text-gray-600">System issues, login problems</p>
              </button>
              <button className="w-full text-left p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                <p className="font-medium">Clinical Support</p>
                <p className="text-sm text-gray-600">Medication queries, clinical advice</p>
              </button>
              <button className="w-full text-left p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                <p className="font-medium">Training Resources</p>
                <p className="text-sm text-gray-600">Video tutorials, user guides</p>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // Main render
  if (!isAuthenticated) {
    return renderLogin();
  }

  return (
    <div className="max-w-md mx-auto bg-white min-h-screen">
      <div className="sticky top-0 bg-white border-b border-gray-200 px-4 py-3 z-10 shadow-sm">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <h1 className="text-xl font-bold bg-gradient-to-r from-green-600 to-teal-600 bg-clip-text text-transparent">MedFast</h1>
            <Heart className="h-5 w-5 text-green-500 animate-pulse" title="Aged Care" />
            <Network className="h-5 w-5 text-blue-500" title="Connected Network" />
            <Shield className="h-5 w-5 text-purple-500" title="Secure & Compliant" />
          </div>
          <div className="flex items-center space-x-2">
            <div className="flex items-center text-xs">
              {networkStatus === 'connected' ? (
                <><Wifi className="h-4 w-4 text-green-500 mr-1" /> Connected</>
              ) : (
                <><WifiOff className="h-4 w-4 text-yellow-500 mr-1 animate-pulse" /> Syncing</>
              )}
            </div>
            <div className="relative">
              <Bell className="h-6 w-6 text-gray-600" />
              {getFilteredAlerts().filter(n => n.status === 'Active').length > 0 && (
                <div className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full flex items-center justify-center animate-pulse">
                  <span className="text-white text-xs">{getFilteredAlerts().filter(n => n.status === 'Active').length}</span>
                </div>
              )}
            </div>
            <div className="text-xs text-gray-600">
              <p className="font-medium">{currentUser?.name}</p>
              <p>{currentUser?.title}</p>
            </div>
            <button 
              onClick={handleLogout}
              className="text-gray-600 hover:text-gray-800"
              title="Logout"
            >
              <LogOut className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>

      <div className="p-4 pb-20">
        {activeTab === 'dashboard' && renderDashboard()}
        {activeTab === 'doctor' && currentUser?.role === 'doctor' && renderDoctorDashboard()}
        {activeTab === 'residents' && renderResidents()}
        {activeTab === 'alerts' && renderAlerts()}
        {activeTab === 'bulk_order' && renderBulkOrder()}
        {activeTab === 'analytics' && renderAnalytics()}
        {activeTab === 'payments' && renderPayments()}
        {activeTab === 'network' && renderNetwork()}
        {activeTab === 'pharmacy' && currentUser?.role === 'pharmacist' && renderPharmacistDashboard()}
      </div>

      <div className="fixed bottom-0 left-1/2 transform -translate-x-1/2 w-full max-w-md bg-white border-t border-gray-200 shadow-lg">
        <div className="flex justify-around py-1">
          {getAvailableTabs().map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex flex-col items-center py-2 px-1 relative transition-all ${
                activeTab === tab.id ? 'text-green-600 transform scale-110' : 'text-gray-500'
              }`}
            >
              <tab.icon className="h-5 w-5" />
              <span className="text-xs mt-1">{tab.label}</span>
              {tab.id === 'bulk_order' && bulkOrders.length > 0 && (
                <div className="absolute -top-1 right-0 w-3 h-3 bg-red-500 text-white text-xs rounded-full flex items-center justify-center animate-pulse">
                  {bulkOrders.length}
                </div>
              )}
              {tab.id === 'alerts' && getFilteredAlerts().filter(a => a.status === 'Active' && a.priority === 'High').length > 0 && (
                <div className="absolute -top-1 right-0 w-3 h-3 bg-red-500 text-white text-xs rounded-full flex items-center justify-center animate-pulse">
                  !
                </div>
              )}
              {tab.id === 'pharmacy' && pharmacistOrders.filter(o => o.status === 'pending').length > 0 && (
                <div className="absolute -top-1 right-0 w-4 h-4 bg-red-500 text-white text-xs rounded-full flex items-center justify-center animate-pulse">
                  {pharmacistOrders.filter(o => o.status === 'pending').length}
                </div>
              )}
              {tab.id === 'doctor' && currentUser?.role === 'doctor' && (
                (() => {
                  const data = getDoctorDashboardData();
                  return data.urgentPrescriptions > 0 ? (
                    <div className="absolute -top-1 right-0 w-3 h-3 bg-orange-500 text-white text-xs rounded-full flex items-center justify-center animate-pulse">
                      {data.urgentPrescriptions}
                    </div>
                  ) : null;
                })()
              )}
              {tab.id === 'payments' && facilityInvoices.filter(inv => inv.status === 'Pending').length > 0 && (
                <div className="absolute -top-1 right-0 w-3 h-3 bg-red-500 text-white text-xs rounded-full flex items-center justify-center animate-pulse">
                  {facilityInvoices.filter(inv => inv.status === 'Pending').length}
                </div>
              )}
            </button>
          ))}
        </div>
      </div>

      {showDrugRegister && renderDrugRegister()}
      {showFamilyConnect && renderFamilyConnect()}
      {showSupport && renderSupport()}
      {showDoctorNote && renderDoctorNote()}
      {rejectionState.showModal && renderRejectionModal()}
    </div>
  );
};

export default MedFastAgedCare;